import { useMemo, useRef, useState } from "react";
import { FaCheck, FaPrint } from "react-icons/fa6";
import "./bill-view-print.style.scss";

// packages
import ReactToPrint from "react-to-print";
import axios from "axios";

// global strings
import strings from "../../../global/strings";

// helper functions
import {
    getSelectedTemplate,
    isEmptyObject,
    splitArray,
} from "../../../global/function";

// billData's real shape is unconfirmed: this component treats the decoded
// portal JWT as a full bill record (billed_items/customer_info/invoice_number/
// company_snapshot), but the only jwt.sign() found in billing_system_BE
// (systemLogin) produces a much smaller {company_info_id, company_user_id,
// company_name, full_name, company_id, scopes} payload, and there's a wired
// but seemingly-unused GET bill/portal/info endpoint that looks like it
// should be fetching the real bill data instead. Typed loosely here rather
// than guessed — preserves current (unverified) runtime behavior exactly.
// Flagged for follow-up investigation, not fixed as part of this batch.
interface BillViewPrintProps {
	billData: Record<string, unknown>;
	token: string | null;
}

interface UpdatePrintCountArgs {
	company_info_id: unknown;
	invoice_number: unknown;
	model_type: string;
	print_type: string;
}

function BillViewPrint({ billData, token }: BillViewPrintProps) {
	const [pageState, setPageState] = useState<"idle" | "loading" | "success">(
		"idle",
	);

	const invoiceBillTemplateRef = useRef<HTMLDivElement>(null);

	const billedItems = billData?.billed_items;

	const slicedArrays = useMemo(
		() =>
			!isEmptyObject(billData) &&
			Array.isArray(billedItems) &&
			billedItems.length > 0
				? splitArray(billedItems, 30, 10)
				: [],
		[billData, billedItems],
	);

	// increase print count of (bill/creditNote) after print or download
	const updatePrintCount = async ({
		company_info_id,
		invoice_number,
		model_type,
		print_type,
	}: UpdatePrintCountArgs) => {
		setPageState("loading");
		const printData = [{ invoice_number, model_type, print_type }];

		try {
			const { data } = await axios.put(
				`${strings.url}bill/portal/update/print`,
				{ company_info_id, printData },
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);
			if (data?.success) {
				setPageState("success");
				setTimeout(() => window.close(), 2000);
			} else {
				setPageState("idle");
				alert("Failed to print the bill. Please try again.");
			}
		} catch (error) {
			console.error("Error printing bill:", error);
			setPageState("idle");
			alert("Failed to print the bill. Please try again.");
		}
	};

	// print new bill
	const handleAfterPrint = async () => {
		await updatePrintCount({
			company_info_id: billData?.company_info_id,
			invoice_number: billData?.invoice_number,
			model_type: "bill",
			print_type: "print",
		});
	};

	// render invoice template with selected template ID for e.g. 1
	const _renderTemplate = (slicedArray: unknown[], index: number) => {
		const selected_bill = {
			...billData,
			...(billData.customer_info as Record<string, unknown> | undefined),
			sliced_items: slicedArray,
		};
		const showBillFooter = index === slicedArrays?.length - 1;

		const page = index + 1 + " of " + slicedArrays?.length;

		const fontStyle = { fontSize: "12px" };

		// preference.template_id is a STRING column on the backend — coerce
		// with Number(...) || 1 rather than ?? 1, since a non-numeric-parsing
		// value must still fall back to the default template.
		const companySnapshot = billData?.company_snapshot as
			| { data?: { preference?: { template_id?: string } } }
			| undefined;
		const templateId = Number(companySnapshot?.data?.preference?.template_id) || 1;

		return getSelectedTemplate(
			templateId,
			selected_bill,
			showBillFooter,
			page,
			fontStyle,
		);
	};

	return (
		<div className="billing-container">
			<h1 className="billing-title">Billing Portal</h1>
			<p className="billing-subtitle">
				Review your bill and print or download the bill
			</p>

			{(pageState === "loading" || !billData?.bill_id) && (
				<div className="status-wrapper">
					<div className="loading-spinner-container">
						<div className="spinner" />
						<p className="spinner-text">Loading...</p>
					</div>
				</div>
			)}

			{pageState === "idle" && (
				<>
					<ReactToPrint
						trigger={() => (
							<div className="button-wrapper">
								<button className="create-button">
									<FaPrint /> Print
								</button>
							</div>
						)}
						content={() => invoiceBillTemplateRef.current}
						onAfterPrint={handleAfterPrint}
					/>
					<div className="portal-template-wrapper">
						{!isEmptyObject(billData) &&
							slicedArrays.length > 0 &&
							slicedArrays.map((slicedArray: unknown[], index: number) => (
								<div
									className="portal-template-container"
									key={index}
								>
									<div className="portal-template-container-inner">
										<div ref={invoiceBillTemplateRef}>
											{_renderTemplate(
												slicedArray,
												index,
											)}
										</div>
									</div>
								</div>
							))}
					</div>
				</>
			)}

			{pageState === "success" && (
				<div className="status-wrapper">
					<div className="success-message">
						<div className="success-icon">
							<FaCheck />
						</div>

						<h2 className="success-heading">
							Bill printed successfully
						</h2>
					</div>
				</div>
			)}
		</div>
	);
}

export default BillViewPrint;
